/**
 * Reducer for ProcFeeConfirm state management
 */

import dayjs from "dayjs";
import { SLASH_FORMAT_DATE } from "constants/format";
import { SESSION_KEY } from "constants/key";
import { EstQuoteDetailTire } from "features/car_model/types";
import { OptionItem, PunctureProtection } from "features/mst_option/types/option";
import {
  CostPriceMap,
  CostSettingInfo,
  SetTireProcFeeStorage,
  SetWheelProcFeeStorage,
  WheelTireCombination,
} from "features/mst_proc_fee/types/proc-fee";
import { TireCostSettings } from "features/mst_proc_fee/utils/priceCalculator";
import {
  calculateTirePrices,
  recalculateWheelPrices,
} from "features/mst_proc_fee/utils/procFeeConfirmHelpers";
import { parseNumber, formatNumber } from "util/helper";

const DISABLED_TEXT = "なし" as const;
const TAKE_OUT_DISABLED_TEXT = "しない" as const;

/**
 * State interface for ProcFeeConfirm
 */
export interface ProcFeeConfirmState {
  customerInfo: {
    customer_name: string;
    customer_tel_no: string;
    quote_by: string;
    exp_date: string;
  };
  costSetting: CostSettingInfo;
  selectedTires: EstQuoteDetailTire[];
  options: {
    nat: OptionItem[];
    lockNat: OptionItem[];
    hub: OptionItem[];
    wcoating: OptionItem[];
  };
  quoteByList: { label: string }[];
  wheelTireCombinations: WheelTireCombination[];
  punctureProtection: PunctureProtection[];
  costPriceMap: CostPriceMap;
}

/**
 * Action types for reducer
 */
export type ProcFeeConfirmAction =
  | {
      type: "SET_CUSTOMER_INFO";
      payload: {
        customer_name?: string;
        customer_tel_no?: string;
        quote_by?: string;
        exp_date?: string;
      };
    }
  | { type: "SET_COST_SETTING"; payload: Partial<CostSettingInfo> }
  | {
      type: "UPDATE_WHEEL_TIRE_DETAIL";
      payload: {
        combinationIndex: number;
        tireIndex: number;
        key: keyof EstQuoteDetailTire;
        value: unknown;
      };
    }
  | {
      type: "UPDATE_WHEEL_REMARKS";
      payload: {
        combinationIndex: number;
        remarks: string;
      };
    }
  | {
      type: "SET_OPTIONS";
      payload: {
        nat?: OptionItem[];
        lockNat?: OptionItem[];
        hub?: OptionItem[];
        wcoating?: OptionItem[];
      };
    }
  | { type: "SET_QUOTE_BY_LIST"; payload: { label: string }[] }
  | { type: "LOAD_FROM_TIRE_SESSION"; payload: SetTireProcFeeStorage }
  | { type: "LOAD_FROM_WHEEL_SESSION"; payload: SetWheelProcFeeStorage }
  | {
      type: "SELECT_ALL_OPTIONS";
      payload: {
        nat: OptionItem[];
        lockNat: OptionItem[];
        hub: OptionItem[];
        wcoating: OptionItem[];
      };
    }
  | { type: "CLEAR_ALL_OPTIONS" }
  | { type: "RECALCULATE_WHEEL_PRICES"; payload: number }
  | {
      type: "UPDATE_TIRE_COMBINATIONS";
      payload: WheelTireCombination[];
    }
  | {
      type: "UPDATE_SPECIAL_PRICES";
      payload: {
        combinationIndex: number;
        tireIndex: number;
        price_tire: number;
        price_wheel: number;
      };
    }
  | { type: "SET_PUNCTURE_PROTECTION"; payload: PunctureProtection[] }
  | { type: "SELECT_ALL_TIRES" }
  | { type: "CLEAR_ALL_TIRES" }
  | { type: "SET_COST_PRICE_MAP"; payload: CostPriceMap };

/**
 * Get initial state with values from sessionStorage
 */
export const getInitialState = (): ProcFeeConfirmState => {
  // Get quote_by from sessionStorage
  let quoteBy = "";
  try {
    const quoteByData = sessionStorage.getItem(SESSION_KEY.QUOTE_BY);
    if (quoteByData) {
      quoteBy = quoteByData;
    }
  } catch (error) {
    // Silently handle error
  }

  return {
    customerInfo: {
      customer_name: "",
      customer_tel_no: "",
      quote_by: quoteBy,
      exp_date: dayjs().add(1, "week").format(SLASH_FORMAT_DATE),
    },
    costSetting: {
      replac_labor_cost: "",
      waste_tire_cost_flag: true,
      waste_tire_cost: "",
      rubber_stem_valve_flag: true,
      rubber_stem_valve_cost: "",
      nitrogen_inflation_flag: false,
      nitrogen_inflation_cost: "",
      hub_rust_prevention_flag: false,
      hub_rust_prevention_cost: "",
      tire_puncture_flag: 0,
      cashback_flag: false,
      cashback_period: 1,
      rounding: 0,
      // wheel
      take_out: "0",
      take_out_flag: true,
      nut: "",
      nut_flag: true,
      lock_nut: "",
      lock_nut_flag: false,
      hub_ring: "",
      hub_ring_flag: false,
      wheel_coating: "",
      wheel_coating_flag: false,
    },
    selectedTires: [],
    options: {
      nat: [],
      lockNat: [],
      hub: [],
      wcoating: [],
    },
    quoteByList: [],
    wheelTireCombinations: [],
    punctureProtection: [],
    costPriceMap: {},
  };
};

/**
 * Reducer function for state management
 * @param state - Current state
 * @param action - Action to dispatch
 * @returns New state
 */
export function procFeeConfirmReducer(
  state: ProcFeeConfirmState,
  action: ProcFeeConfirmAction,
): ProcFeeConfirmState {
  switch (action.type) {
    case "SET_CUSTOMER_INFO":
      return {
        ...state,
        customerInfo: { ...state.customerInfo, ...action.payload },
      };

    case "SET_COST_SETTING":
      return {
        ...state,
        costSetting: { ...state.costSetting, ...action.payload },
      };

    case "UPDATE_WHEEL_TIRE_DETAIL": {
      const { combinationIndex, tireIndex, key, value } = action.payload;
      const updatedCombinations = [...state.wheelTireCombinations];
      const combination = { ...updatedCombinations[combinationIndex] };

      if (combination && combination.tires[tireIndex]) {
        const updatedTires = [...combination.tires];
        updatedTires[tireIndex] = {
          ...updatedTires[tireIndex],
          [key]: value,
        };
        combination.tires = updatedTires;
        updatedCombinations[combinationIndex] = combination;
      }

      return {
        ...state,
        wheelTireCombinations: updatedCombinations,
      };
    }

    case "UPDATE_WHEEL_REMARKS": {
      const { combinationIndex, remarks } = action.payload;
      const updatedCombinations = [...state.wheelTireCombinations];
      const combination = { ...updatedCombinations[combinationIndex] };

      if (combination) {
        combination.wheel = {
          ...combination.wheel,
          remarks,
        };
        updatedCombinations[combinationIndex] = combination;
      }

      return {
        ...state,
        wheelTireCombinations: updatedCombinations,
      };
    }

    case "SET_OPTIONS":
      return {
        ...state,
        options: {
          nat: action.payload.nat ?? state.options.nat,
          lockNat: action.payload.lockNat ?? state.options.lockNat,
          hub: action.payload.hub ?? state.options.hub,
          wcoating: action.payload.wcoating ?? state.options.wcoating,
        },
      };

    case "SET_QUOTE_BY_LIST":
      return {
        ...state,
        quoteByList: action.payload,
      };

    case "LOAD_FROM_TIRE_SESSION": {
      const { proc_fees, cashback, tbl_quote_detail_tire } = action.payload;

      // Helper function to safely get value and checked from PrcFeesData
      const getValue = (field: keyof typeof proc_fees) => proc_fees?.[field]?.value;
      const getChecked = (field: keyof typeof proc_fees) => proc_fees?.[field]?.checked ?? false;

      return {
        ...state,
        costSetting: {
          ...state.costSetting,
          // Map values with format
          replac_labor_cost: formatNumber(getValue("replac_labor_cost")),
          waste_tire_cost: formatNumber(getValue("waste_tire_cost")),
          rubber_stem_valve_cost: formatNumber(getValue("rubber_stem_valve_cost")),
          nitrogen_inflation_cost: formatNumber(getValue("nitrogen_inflation_cost")),
          hub_rust_prevention_cost: formatNumber(getValue("hub_rust_prevention_cost")),
          // Map checked flags
          waste_tire_cost_flag: getChecked("waste_tire_cost"),
          rubber_stem_valve_flag: getChecked("rubber_stem_valve_cost"),
          nitrogen_inflation_flag: getChecked("nitrogen_inflation_cost"),
          hub_rust_prevention_flag: getChecked("hub_rust_prevention_cost"),
          // Map cashback
          tire_puncture_flag: cashback?.puncture ?? 0,
          cashback_period: cashback?.cashback_period ?? 1,
          cashback_flag: (cashback?.cashback_period ?? 1) > 0,
        },
        selectedTires: tbl_quote_detail_tire || state.selectedTires,
      };
    }

    case "LOAD_FROM_WHEEL_SESSION": {
      const { cost_setting, wheels_detail, discount_rate, cost_price_map } = action.payload;

      // Create combinations: each wheel paired with all tires
      const combinations: WheelTireCombination[] = (wheels_detail || []).map((wheel: any) => {
        // Find matching discount_rate by id
        const matchingDiscount = (discount_rate || []).find((dr: any) => dr.id === wheel.id);

        // If found, update fixed_discount, otherwise keep original
        const updatedWheel = matchingDiscount
          ? {
              ...wheel,
              fixed_discount: parseNumber(matchingDiscount.value),
            }
          : wheel;

        return {
          wheel: updatedWheel,
          tires: state.selectedTires,
        };
      });

      return {
        ...state,
        costSetting: {
          ...state.costSetting,
          // Only merge wheel-related fields from cost_setting
          // Don't overwrite tire-related fields that were loaded from SELECTED_TIRE
          take_out: cost_setting.take_out ?? state.costSetting.take_out,
          take_out_flag: cost_setting.take_out_flag ?? state.costSetting.take_out_flag,
          nut: cost_setting.nut ?? state.costSetting.nut,
          nut_flag: cost_setting.nut_flag ?? state.costSetting.nut_flag,
          lock_nut: cost_setting.lock_nut ?? state.costSetting.lock_nut,
          lock_nut_flag: cost_setting.lock_nut_flag ?? state.costSetting.lock_nut_flag,
          hub_ring: cost_setting.hub_ring ?? state.costSetting.hub_ring,
          hub_ring_flag: cost_setting.hub_ring_flag ?? state.costSetting.hub_ring_flag,
          wheel_coating: cost_setting.wheel_coating ?? state.costSetting.wheel_coating,
          wheel_coating_flag:
            cost_setting.wheel_coating_flag ?? state.costSetting.wheel_coating_flag,
        },
        wheelTireCombinations: combinations,
        costPriceMap: cost_price_map ?? state.costPriceMap,
      };
    }

    case "SELECT_ALL_OPTIONS": {
      const { nat, lockNat, hub, wcoating } = action.payload;
      return {
        ...state,
        costSetting: {
          ...state.costSetting,
          waste_tire_cost_flag: true,
          rubber_stem_valve_flag: true,
          nitrogen_inflation_flag: true,
          hub_rust_prevention_flag: true,
          cashback_flag: true,
          take_out_flag: true,
          nut_flag: true,
          lock_nut_flag: true,
          hub_ring_flag: true,
          wheel_coating_flag: true,
          nitrogen_inflation_cost: state.costSetting.nitrogen_inflation_cost || "500",
          hub_rust_prevention_cost: state.costSetting.hub_rust_prevention_cost || "1,000",
          take_out: "0",
          nut: String(nat[0]?.option_cd || nat[0]?.id || ""),
          lock_nut: String(lockNat[0]?.option_cd || lockNat[0]?.id || ""),
          hub_ring: String(hub[0]?.option_cd || hub[0]?.id || ""),
          wheel_coating: String(wcoating[0]?.option_cd || wcoating[0]?.id || ""),
        },
      };
    }

    case "CLEAR_ALL_OPTIONS":
      return {
        ...state,
        costSetting: {
          ...state.costSetting,
          waste_tire_cost_flag: false,
          rubber_stem_valve_flag: false,
          nitrogen_inflation_flag: false,
          hub_rust_prevention_flag: false,
          cashback_flag: false,
          take_out_flag: false,
          nut_flag: false,
          lock_nut_flag: false,
          hub_ring_flag: false,
          wheel_coating_flag: false,
          nitrogen_inflation_cost: "",
          hub_rust_prevention_cost: "",
          take_out: TAKE_OUT_DISABLED_TEXT,
          nut: DISABLED_TEXT,
          lock_nut: DISABLED_TEXT,
          hub_ring: DISABLED_TEXT,
          wheel_coating: DISABLED_TEXT,
        },
      };

    case "RECALCULATE_WHEEL_PRICES":
      return {
        ...state,
        wheelTireCombinations: recalculateWheelPrices(
          state.wheelTireCombinations,
          action.payload,
          state.costPriceMap,
        ),
      };

    case "UPDATE_TIRE_COMBINATIONS":
      return {
        ...state,
        wheelTireCombinations: action.payload,
      };

    case "UPDATE_SPECIAL_PRICES": {
      const { combinationIndex, tireIndex, price_tire } = action.payload;
      const updatedCombinations = [...state.wheelTireCombinations];
      const combination = { ...updatedCombinations[combinationIndex] };

      if (combination) {
        // Update tire discounted_sale_price and recalculate all prices
        if (price_tire > 0 && combination.tires[tireIndex]) {
          const updatedTires = [...combination.tires];
          const tireToUpdate = { ...updatedTires[tireIndex] };

          // Update discounted_sale_price from special price API and mark it
          tireToUpdate.discounted_sale_price = price_tire;
          tireToUpdate.has_special_price = true; // Mark this tire as having special price

          // Recalculate all tire prices using calculateTirePrices
          const tireCostSettings: TireCostSettings = {
            replac_labor_cost: parseNumber(state.costSetting.replac_labor_cost),
            waste_tire_cost: state.costSetting.waste_tire_cost_flag
              ? parseNumber(state.costSetting.waste_tire_cost)
              : 0,
            rubber_stem_valve_cost: state.costSetting.rubber_stem_valve_flag
              ? parseNumber(state.costSetting.rubber_stem_valve_cost)
              : 0,
            nitrogen_inflation_cost: state.costSetting.nitrogen_inflation_flag
              ? parseNumber(state.costSetting.nitrogen_inflation_cost)
              : 0,
            hub_rust_prevention_cost: state.costSetting.hub_rust_prevention_flag
              ? parseNumber(state.costSetting.hub_rust_prevention_cost)
              : 0,
            tire_puncture_flag: state.costSetting.tire_puncture_flag,
            cashback_flag: state.costSetting.cashback_flag,
            cashback_period: Number.isNaN(state.costSetting.cashback_period)
              ? 1
              : Number(state.costSetting.cashback_period),
          };

          // Preserve the special price from API (pass true to preserveDiscountedPrice)
          const recalculatedTire = calculateTirePrices(
            tireToUpdate,
            tireCostSettings,
            state.punctureProtection,
            true, // Preserve special price from API
            state.costPriceMap,
          );
          updatedTires[tireIndex] = recalculatedTire;
          combination.tires = updatedTires;
        }

        // TODO:Update wheel discounted_price and recalculate final_price
        // if (price_wheel > 0) {
        //   const optionsPrice =
        //     combination.wheel.final_price - (combination.wheel.discounted_price || 0);
        //   combination.wheel = {
        //     ...combination.wheel,
        //     discounted_price: price_wheel,
        //     final_price: price_wheel + optionsPrice,
        //   };
        // }

        updatedCombinations[combinationIndex] = combination;
      }

      return {
        ...state,
        wheelTireCombinations: updatedCombinations,
      };
    }

    case "SET_PUNCTURE_PROTECTION":
      return {
        ...state,
        punctureProtection: action.payload,
      };

    case "SELECT_ALL_TIRES": {
      const updatedCombinations = state.wheelTireCombinations.map((combination) => ({
        ...combination,
        tires: combination.tires.map((tire) => ({
          ...tire,
          checked: true,
        })),
      }));

      return {
        ...state,
        wheelTireCombinations: updatedCombinations,
      };
    }

    case "CLEAR_ALL_TIRES": {
      const updatedCombinations = state.wheelTireCombinations.map((combination) => ({
        ...combination,
        tires: combination.tires.map((tire) => ({
          ...tire,
          checked: false,
        })),
      }));

      return {
        ...state,
        wheelTireCombinations: updatedCombinations,
      };
    }

    case "SET_COST_PRICE_MAP":
      return {
        ...state,
        costPriceMap: action.payload,
      };

    default:
      return state;
  }
}
